import React, { useEffect, useState } from 'react';
import { contentApi } from '../../services/contentApi';
import { userApi, AdminUser } from '../../services/api';
import { CourseListItem, CourseAdminGrant, CourseInvite } from '../../types/content';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import { isOwner, isCourseAdmin, isCourseOwner } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';
import ActionStatus from '../../components/UI/ActionStatus';
import { useAutoClear } from '../../hooks/useAutoClear';

const LearnersAdminPage: React.FC = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [courseId, setCourseId] = useState<number | ''>('');
  const [bulk, setBulk] = useState('');
  const [issueCodes, setIssueCodes] = useState(true);
  const [latestCodes, setLatestCodes] = useState<CourseInvite[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [invites, setInvites] = useState<CourseInvite[]>([]);
  const [grants, setGrants] = useState<CourseAdminGrant[]>([]);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [grantUserId, setGrantUserId] = useState<number | ''>('');
  const [perms, setPerms] = useState({ canEdit: true, canPublish: false, canOnboard: true, canReview: false });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [grantMessage, setGrantMessage] = useState<string | null>(null);
  useAutoClear(statusMessage, setStatusMessage, 8000);
  useAutoClear(grantMessage, setGrantMessage, 5000);
  useAutoClear(error, setError, 10000);
  const owner = isOwner(user);
  const courseAdmin = isCourseAdmin(user);
  /** CourseOwner without Owner/CourseAdmin — invitations limited to courses they authored. */
  const courseOwnerOnly = isCourseOwner(user) && !courseAdmin && !owner;

  useEffect(() => {
    contentApi.myCourses({ pageSize: 200 })
      .then((r) => {
        let list = r.data.courses || [];
        if (courseOwnerOnly) {
          list = list.filter((course) => course.author?.authorId === user?.id);
        }
        list = [...list].sort((a, b) => a.title.localeCompare(b.title));
        setCourses(list);
        setCourseId((current) =>
          current && list.some((c) => c.id === current) ? current : (list[0]?.id ?? '')
        );
      })
      .catch(() => {
        setCourses([]);
        setCourseId('');
      });
  }, [courseOwnerOnly, user?.id]);

  useEffect(() => {
    if (!owner) return;
    userApi.adminListUsers()
      .then((r) => setAdmins((r.data.users || []).filter((item) => (item.role || '').toLowerCase() === 'courseadmin')))
      .catch(() => undefined);
  }, [owner]);

  const selected = Number(courseId) || 0;

  const refresh = () => {
    if (!selected) return;
    contentApi.listCourseInvites(selected).then((r) => setInvites(r.data.invites || [])).catch(() => undefined);
    if (owner) {
      contentApi.listCourseAdmins(selected).then((r) => setGrants(r.data.grants || [])).catch(() => undefined);
    }
  };

  useEffect(() => { refresh(); }, [selected, owner]);

  const saveGrant = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !grantUserId) return;
    const admin = admins.find((item) => item.id === grantUserId);
    await contentApi.saveCourseAdmin(selected, { userProfileId: Number(grantUserId), userEmail: admin?.email, ...perms });
    setGrantMessage('Course admin grant updated.');
    refresh();
  };

  if (!isCourseAdmin(user) && !owner && !isCourseOwner(user)) {
    return <p className="text-gray-600">Invitations are limited to the course owner, Course Admin, or Owner.</p>;
  }

  const downloadTemplate = () => {
    const csv = 'email\nstudent1@example.com\nstudent2@example.com\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'course-invite-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const parseEmails = (raw: string) =>
    raw.split(/[\s,;]+/).map((item) => item.trim()).filter((item) => item.includes('@') && item.toLowerCase() !== 'email');

  const onboardMany = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    const emails = parseEmails(bulk);
    if (!emails.length) {
      setError('Add at least one email, or upload the template.');
      return;
    }
    setError(null);
    setStatusMessage(null);
    setLatestCodes([]);
    setSaving(true);
    try {
      const response = await contentApi.inviteCourseEmails(selected, emails, issueCodes);
      setBulk('');
      refresh();
      const issued = response.data.invites || [];
      setLatestCodes(issued.filter((invite) => invite.code));
      setStatusMessage(response.data.message || `Processed ${response.data.created || emails.length} learner(s).`);
      if (response.data.skipped?.length) {
        setError(`Could not onboard: ${response.data.skipped.join(', ')}`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not onboard those students.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <label className="text-sm text-gray-700 block max-w-md">
        Course
        <select className="mt-1 w-full rounded-md border-gray-300" value={courseId} onChange={(e) => setCourseId(e.target.value ? Number(e.target.value) : '')}>
          {courses.map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}
        </select>
      </label>
      {error && !statusMessage && <p className="text-error-600 text-sm">{error}</p>}

      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold text-gray-900">Onboard learners by email</h2>
            <p className="text-sm text-gray-600 mt-1">
              Invite one or many emails. With codes: learners redeem a one-time code (skips payment). Without codes: existing accounts are enrolled directly.
            </p>
          </div>
          <button type="button" className="text-sm font-semibold text-primary-700" onClick={downloadTemplate}>Download email template</button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={onboardMany}>
          <textarea
            className="w-full rounded-md border-gray-300 text-sm"
            rows={5}
            value={bulk}
            onChange={(e) => setBulk(e.target.value)}
            placeholder="one@example.com&#10;two@example.com"
          />
          <input
            type="file"
            accept=".csv,text/csv,text/plain"
            className="text-sm"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setBulk(await file.text());
            }}
          />
          <label className="inline-flex items-center gap-2 text-sm text-gray-800">
            <input type="checkbox" checked={issueCodes} onChange={(e) => setIssueCodes(e.target.checked)} />
            Issue one-time access codes
          </label>
          <div>
            <Button type="submit" disabled={saving}>{saving ? 'Onboarding…' : 'Onboard learners'}</Button>
            <ActionStatus message={statusMessage} error={error} />
          </div>
        </form>
        {latestCodes.length > 0 && (
          <div className="mt-4 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1">
            <p className="font-semibold text-emerald-900">Share these codes now (they will not be shown again):</p>
            {latestCodes.map((invite) => (
              <p key={invite.email} className="font-mono">
                {invite.email}: <span className="font-semibold tracking-wide">{invite.code}</span>
              </p>
            ))}
          </div>
        )}
        <ul className="mt-4 space-y-2 text-sm">
          {invites.map((invite) => (
            <li key={invite.id} className="flex justify-between gap-3 border-b pb-2">
              <span>{invite.email} · hint {invite.codeHint}</span>
              <span className="text-gray-500">{invite.isRedeemed ? 'Used' : 'Unused'}</span>
            </li>
          ))}
        </ul>
      </Card>

      {owner && (
        <Card className="p-5">
          <h2 className="font-semibold text-gray-900">Course Admin permissions</h2>
          <p className="text-sm text-gray-600 mt-1">Grant edit, publish, onboard, or review for this course only.</p>
          <form className="mt-4 grid md:grid-cols-2 gap-3" onSubmit={saveGrant}>
            <label className="text-sm text-gray-700">
              Course Admin
              <select className="mt-1 w-full rounded-md border-gray-300" value={grantUserId} onChange={(e) => setGrantUserId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Select</option>
                {admins.map((admin) => <option key={admin.id} value={admin.id}>{admin.email}</option>)}
              </select>
            </label>
            <div className="flex flex-wrap gap-3 items-end text-sm">
              {(['canEdit', 'canPublish', 'canOnboard', 'canReview'] as const).map((key) => (
                <label key={key} className="flex items-center gap-2">
                  <input type="checkbox" checked={perms[key]} onChange={(e) => setPerms({ ...perms, [key]: e.target.checked })} />
                  {key.replace('can', '')}
                </label>
              ))}
              <Button type="submit" size="sm">Save grant</Button>
            </div>
          </form>
          <ActionStatus message={grantMessage} />
          <ul className="mt-4 space-y-2 text-sm">
            {grants.map((grant) => (
              <li key={grant.id} className="flex justify-between gap-3 border-b pb-2">
                <span>{grant.userEmail || grant.userProfileId} · Edit {grant.canEdit ? 'yes' : 'no'} · Publish {grant.canPublish ? 'yes' : 'no'} · Onboard {grant.canOnboard ? 'yes' : 'no'} · Review {grant.canReview ? 'yes' : 'no'}</span>
                <button type="button" className="text-error-600" onClick={() => contentApi.removeCourseAdmin(selected, grant.id).then(refresh)}>Remove</button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

export default LearnersAdminPage;
